import React, { useState, useRef, useCallback } from 'react';
import { Box, Button, Typography, Paper, Snackbar, Alert, useTheme, Stack } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import CompressIcon from '@mui/icons-material/Compress';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import JsonView from '@uiw/react-json-view';
import { vscodeTheme } from '@uiw/react-json-view/vscode';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import MonacoEditor from '../../components/MonacoEditor.js';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const sampleJson = {
  "id": "0001", "type": "donut", "name": "Cake", "ppu": 0.55,
  "batters": { "batter": [{ "id": "1001", "type": "Regular" }, { "id": "1002", "type": "Chocolate" }] },
  "topping": [{ "id": "5001", "type": "None" }, { "id": "5002", "type": "Glazed" }]
};

function JsonViewer() {
  const theme = useTheme();
  const { mode } = theme.palette;

  const [jsonInput, setJsonInput] = useState(JSON.stringify(sampleJson, null, 2));
  const [parsedJson, setParsedJson] = useState(sampleJson);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const editorRef = useRef(null);

  const handleJsonInputChange = useCallback((value) => {
    setJsonInput(value);
    try {
      if (value.trim() === '') {
        setParsedJson(null);
        setError(null);
      } else {
        const parsed = JSON.parse(value);
        setParsedJson(parsed);
        setError(null);
      }
    } catch (e) {
      setParsedJson(null);
      setError(e.message);
    }
  }, []);

  const formatJson = (indent) => {
    try {
      const parsed = JSON.parse(jsonInput);
      const formatted = JSON.stringify(parsed, null, indent);
      setJsonInput(formatted);
      setSnackbar({ open: true, message: indent ? 'JSON Formatted' : 'JSON Minified' });
    } catch (e) {
      setError(e.message);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonInput)
      .then(() => setSnackbar({ open: true, message: 'Copied to clipboard!' }))
      .catch(() => setSnackbar({ open: true, message: 'Failed to copy.' }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const text = await file.text();
      setJsonInput(text);
      handleJsonInputChange(text);
    }
    event.target.value = null;
  };

  const handleDownload = () => {
    const blob = new Blob([jsonInput], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'data.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleResize = useCallback(() => {
    setTimeout(() => editorRef.current?.layout(), 0);
  }, []);

  return (
    <>
      <SEO
        title="Free Online JSON Viewer & Formatter | UmbrellaCo"
        description="Format, beautify, and validate JSON online for free. Interactive JSON tree viewer with copy, download, and syntax highlighting features."
        keywords="json viewer, json formatter, json beautifier, validate json, json editor online"
        url="/json-tools/viewer"
        jsonLd={createSoftwareAppSchema(
          'JSON Viewer & Formatter',
          'Format, beautify, and validate JSON online for free. Interactive JSON tree viewer.',
          'https://umbrellaco.in/json-tools/viewer',
          ['JSON Formatting', 'JSON Minification', 'Tree Viewer', 'Syntax Validation']
        )}
      />
      <ToolPageContainer
        title="JSON Viewer & Formatter"
        description="Paste your JSON below to automatically validate, format, and visualize it in a tree structure."
        disablePadding
      >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      <PanelGroup direction="horizontal" onLayout={handleResize}>
        <Panel defaultSize={50} minSize={30}>
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', minHeight: 0 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button onClick={() => formatJson(2)} startIcon={<FormatAlignLeftIcon />} disabled={!!error}>Format</Button>
              <Button onClick={() => formatJson(0)} startIcon={<CompressIcon />} disabled={!!error}>Minify</Button>
              <Button onClick={handleCopy} startIcon={<ContentCopyIcon />}>Copy</Button>
              <Button component="label" startIcon={<UploadFileIcon />}>Upload<input type="file" hidden onChange={handleFileUpload} accept=".json" /></Button>
              <Button onClick={handleDownload} startIcon={<DownloadIcon />} disabled={!!error}>Download</Button>
              <Button onClick={() => handleJsonInputChange('')} startIcon={<ClearIcon />}>Clear</Button>
            </Stack>
            <Paper
              variant="outlined"
              sx={{ flexGrow: 1, borderColor: error ? 'error.main' : 'divider', position: 'relative', overflow: 'hidden', minHeight: 0 }}
            >
              <MonacoEditor
                ref={editorRef}
                language="json"
                value={jsonInput}
                onChange={handleJsonInputChange}
              />
              {error && (
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'error.main', color: 'error.contrastText', p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorOutlineIcon fontSize="small" />
                  <Typography variant="body2" component="span">{error}</Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Panel>
        <PanelResizeHandle style={{ width: '8px', background: theme.palette.background.paper, borderLeft: `1px solid ${theme.palette.divider}`, borderRight: `1px solid ${theme.palette.divider}` }} />
        <Panel defaultSize={50} minSize={30}>
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', minHeight: 0 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Tree View</Typography>
            <Paper variant="outlined" sx={{ flexGrow: 1, p: 2, overflow: 'auto' }}>
              {error ? (
                <Alert severity="warning">Invalid JSON. Cannot display tree view.</Alert>
              ) : parsedJson ? (
                <JsonView
                  value={parsedJson}
                  style={mode === 'dark' ? vscodeTheme.dark : vscodeTheme.light}
                  displayDataTypes={true}
                  enableClipboard={true}
                  collapsed={2}
                />
              ) : (
                <Alert severity="info">Editor is empty.</Alert>
              )}
            </Paper>
          </Box>
        </Panel>
      </PanelGroup>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} />
      </Box>
      </ToolPageContainer>
    </>
  );
}

export default JsonViewer;